import {
	Activity,
	AlertCircle,
	AlertTriangle,
	CheckCircle,
	Download,
	Eye,
	Shield,
	Users,
	XCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { RoleBadge } from '../components/RoleBadge';
import { useAuth } from '../contexts/AuthContext';
import { adminApi } from '../lib/admin-api';
import { exportSecurityEvents } from '../lib/export-utils';

/**
 * Security event interface
 */
interface SecurityEvent {
	id: string;
	timestamp: Date;
	type: 'LOGIN' | 'ROLE_CHANGE' | 'FAILED_LOGIN' | 'WORKFLOW_DELETE' | 'USER_DEACTIVATE';
	severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
	user: {
		email: string;
		name: string;
		role: string;
	};
	description: string;
	metadata?: any;
}

/**
 * Security metrics interface
 */
interface SecurityMetrics {
	totalUsers: number;
	activeUsers: number;
	failedLogins24h: number;
	roleChanges7d: number;
	securityEvents30d: number;
	threatLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

/**
 * Security event card component
 */
function SecurityEventCard({ event }: { event: SecurityEvent }) {
	const getSeverityColor = (severity: string) => {
		switch (severity) {
			case 'LOW':
				return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200';
			case 'MEDIUM':
				return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200';
			case 'HIGH':
				return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-200';
			case 'CRITICAL':
				return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200';
			default:
				return 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-200';
		}
	};

	const getEventIcon = (type: string) => {
		switch (type) {
			case 'LOGIN':
				return CheckCircle;
			case 'FAILED_LOGIN':
				return XCircle;
			case 'ROLE_CHANGE':
				return Users;
			case 'WORKFLOW_DELETE':
				return AlertTriangle;
			case 'USER_DEACTIVATE':
				return AlertCircle;
			default:
				return Activity;
		}
	};

	const Icon = getEventIcon(event.type);

	return (
		<div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
			<div className="flex items-start space-x-3">
				<div className={`p-2 rounded-full ${getSeverityColor(event.severity)}`}>
					<Icon className="w-4 h-4" />
				</div>

				<div className="flex-1 min-w-0">
					<div className="flex items-center justify-between">
						<p className="text-sm font-medium text-gray-900 dark:text-white">
							{event.type.replace('_', ' ')}
						</p>
						<span
							className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(
								event.severity
							)}`}
						>
							{event.severity}
						</span>
					</div>

					<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
						{event.description}
					</p>

					<div className="flex items-center mt-2 space-x-3 text-xs text-gray-500 dark:text-gray-400">
						<span>{event.user.email}</span>
						<span>•</span>
						<RoleBadge role={event.user.role} size="xs" showTooltip={false} />
						<span>•</span>
						<span>{new Date(event.timestamp).toLocaleTimeString()}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

/**
 * Security metrics card component
 */
function MetricsCard({
	title,
	value,
	change,
	icon: Icon,
	color = 'blue',
}: {
	title: string;
	value: string | number;
	change?: string;
	icon: any;
	color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple';
}) {
	const colors = {
		blue: 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-200',
		green: 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200',
		yellow: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200',
		red: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200',
		purple: 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
	};

	return (
		<div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
			<div className="p-5">
				<div className="flex items-center">
					<div className="flex-shrink-0">
						<div className={`p-3 rounded-md ${colors[color]}`}>
							<Icon className="h-6 w-6" />
						</div>
					</div>
					<div className="ml-5 w-0 flex-1">
						<dl>
							<dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
								{title}
							</dt>
							<dd className="flex items-baseline">
								<div className="text-2xl font-semibold text-gray-900 dark:text-white">
									{value}
								</div>
								{change && (
									<div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
										{change}
									</div>
								)}
							</dd>
						</dl>
					</div>
				</div>
			</div>
		</div>
	);
}

/**
 * Security Dashboard - Real-time monitoring and alerts
 */
export function SecurityDashboard() {
	const { user } = useAuth();
	const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
	const [eventFilter, setEventFilter] = useState<'all' | 'critical' | 'failed_logins'>('all');
	const [securityData, setSecurityData] = useState<{
		events: SecurityEvent[];
		metrics: SecurityMetrics;
	} | null>(null);
	const [loading, setLoading] = useState(true);

	// Load security data from API with duplicate call prevention
	useEffect(() => {
		let isCancelled = false;

		const loadSecurityData = async () => {
			if (isCancelled) return;

			try {
				setLoading(true);
				const data = await adminApi.getSecurityEvents({
					timeRange,
					eventType: eventFilter,
				});

				if (!isCancelled) {
					setSecurityData(data);
				}
			} catch (error) {
				if (!isCancelled) {
					console.error('Failed to load security data:', error);
					// Fallback to mock data
					setSecurityData({
						events: [],
						metrics: {
							totalUsers: 0,
							activeUsers: 0,
							failedLogins24h: 0,
							roleChanges7d: 0,
							securityEvents30d: 0,
							threatLevel: 'LOW',
						},
					});
				}
			} finally {
				if (!isCancelled) {
					setLoading(false);
				}
			}
		};

		loadSecurityData();

		// Cleanup function to prevent state updates if component unmounts
		return () => {
			isCancelled = true;
		};
	}, [timeRange, eventFilter]);

	// Use real data or fallback to empty array
	const events = securityData?.events || [];
	const metrics = securityData?.metrics || {
		totalUsers: 0,
		activeUsers: 0,
		failedLogins24h: 0,
		roleChanges7d: 0,
		securityEvents30d: 0,
		threatLevel: 'LOW' as const,
	};

	const filteredEvents = events.filter(event => {
		if (eventFilter === 'all') return true;
		if (eventFilter === 'critical')
			return event.severity === 'CRITICAL' || event.severity === 'HIGH';
		if (eventFilter === 'failed_logins') return event.type === 'FAILED_LOGIN';
		return true;
	});

	return (
		<div className="p-8 max-w-7xl mx-auto">
			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
							<Shield className="mr-3 w-8 h-8 text-blue-600" />
							Security Dashboard
						</h1>
						<p className="mt-2 text-gray-600 dark:text-gray-400">
							Real-time security monitoring and threat detection
						</p>
					</div>

					<div className="flex items-center space-x-3">
						<select
							value={timeRange}
							onChange={e => setTimeRange(e.target.value as any)}
							className="px-3 py-2 border border-gray-300 rounded-md bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
						>
							<option value="1h">Last Hour</option>
							<option value="24h">Last 24 Hours</option>
							<option value="7d">Last 7 Days</option>
							<option value="30d">Last 30 Days</option>
						</select>

						<button
							onClick={() => exportSecurityEvents(filteredEvents)}
							className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
						>
							<Download className="mr-2 w-4 h-4" />
							Export Security Events
						</button>
					</div>
				</div>
			</div>

			{/* Security Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
				<MetricsCard
					title="Total Users"
					value={metrics.totalUsers}
					icon={Users}
					color="blue"
				/>
				<MetricsCard
					title="Active Users"
					value={metrics.activeUsers}
					change="+2 this week"
					icon={Activity}
					color="green"
				/>
				<MetricsCard
					title="Failed Logins (24h)"
					value={metrics.failedLogins24h}
					icon={XCircle}
					color="red"
				/>
				<MetricsCard
					title="Role Changes (7d)"
					value={metrics.roleChanges7d}
					icon={Shield}
					color="purple"
				/>
				<MetricsCard
					title="Security Events (30d)"
					value={metrics.securityEvents30d}
					icon={AlertTriangle}
					color="yellow"
				/>
			</div>

			{/* Threat Level Indicator */}
			<div className="mb-8">
				<div
					className={`rounded-lg p-4 ${
						metrics.threatLevel === 'LOW'
							? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
							: metrics.threatLevel === 'MEDIUM'
							? 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
							: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
					}`}
				>
					<div className="flex items-center">
						<Shield
							className={`h-5 w-5 mr-2 ${
								metrics.threatLevel === 'LOW'
									? 'text-green-600'
									: metrics.threatLevel === 'MEDIUM'
									? 'text-yellow-600'
									: 'text-red-600'
							}`}
						/>
						<h3
							className={`text-sm font-medium ${
								metrics.threatLevel === 'LOW'
									? 'text-green-800 dark:text-green-200'
									: metrics.threatLevel === 'MEDIUM'
									? 'text-yellow-800 dark:text-yellow-200'
									: 'text-red-800 dark:text-red-200'
							}`}
						>
							Current Threat Level: {metrics.threatLevel}
						</h3>
					</div>
					<p
						className={`mt-1 text-sm ${
							metrics.threatLevel === 'LOW'
								? 'text-green-700 dark:text-green-300'
								: metrics.threatLevel === 'MEDIUM'
								? 'text-yellow-700 dark:text-yellow-300'
								: 'text-red-700 dark:text-red-300'
						}`}
					>
						{metrics.threatLevel === 'LOW' &&
							'All systems operating normally. No security threats detected.'}
						{metrics.threatLevel === 'MEDIUM' &&
							'Some suspicious activity detected. Monitoring closely.'}
						{metrics.threatLevel === 'HIGH' &&
							'Multiple security events detected. Immediate attention required.'}
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				{/* Live Activity Feed */}
				<div className="lg:col-span-2">
					<div className="bg-white dark:bg-gray-800 shadow rounded-lg">
						<div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
									<Activity className="mr-2 w-5 h-5 text-blue-600" />
									Live Security Events
								</h3>

								<div className="flex items-center space-x-2">
									<select
										value={eventFilter}
										onChange={e => setEventFilter(e.target.value as any)}
										className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-white"
									>
										<option value="all">All Events</option>
										<option value="critical">Critical Only</option>
										<option value="failed_logins">Failed Logins</option>
									</select>

									<div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
										<div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
										Live
									</div>
								</div>
							</div>
						</div>

						<div className="p-6">
							<div className="space-y-4 max-h-96 overflow-y-auto">
								{filteredEvents.length === 0 ? (
									<div className="text-center py-8">
										<Shield className="mx-auto h-12 w-12 text-gray-400" />
										<h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
											No security events
										</h3>
										<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
											No events match your current filter.
										</p>
									</div>
								) : (
									filteredEvents.map(event => (
										<SecurityEventCard key={event.id} event={event} />
									))
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Security Summary Panel */}
				<div className="space-y-6">
					{/* Current User Info */}
					<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
						<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
							<Eye className="mr-2 w-5 h-5 text-blue-600" />
							Your Access
						</h3>

						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									Role:
								</span>
								<RoleBadge
									role={user?.roles?.[0] || 'READ'}
									size="sm"
									showIcon={true}
								/>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									Status:
								</span>
								<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
									Active
								</span>
							</div>

							<div className="flex items-center justify-between">
								<span className="text-sm text-gray-600 dark:text-gray-400">
									Last Login:
								</span>
								<span className="text-sm text-gray-900 dark:text-white">
									Just now
								</span>
							</div>
						</div>
					</div>

					{/* Quick Actions */}
					<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
						<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
							Quick Actions
						</h3>

						<div className="space-y-3">
							<button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
								<Users className="mr-2 w-4 h-4" />
								Manage Users
							</button>

							<button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
								<Download className="mr-2 w-4 h-4" />
								Export Audit Logs
							</button>

							<button className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
								<AlertTriangle className="mr-2 w-4 h-4" />
								Security Settings
							</button>
						</div>
					</div>

					{/* Recent Role Changes */}
					<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
						<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
							Recent Role Changes
						</h3>

						<div className="space-y-3">
							<div className="flex items-center justify-between py-2">
								<div>
									<div className="text-sm font-medium text-gray-900 dark:text-white">
										josh@vquip.com
									</div>
									<div className="text-xs text-gray-500 dark:text-gray-400">
										2 hours ago
									</div>
								</div>
								<div className="flex items-center space-x-2">
									<RoleBadge role="USER" size="xs" showTooltip={false} />
									<span className="text-gray-400">→</span>
									<RoleBadge role="ADMIN" size="xs" showTooltip={false} />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
